import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export type ProviderIntegrationEnvironment = 'dev' | 'prod';
export interface ProviderIntegrationOperation {
  target_kind: 'MIDTERM' | 'GENERAL';
  transport: 'UNKNOWN' | 'DELIVERED';
  readback_matches: boolean | null;
  terminal_state: 'PENDING' | 'UNKNOWN' | 'APPLIED' | 'NOT_APPLIED';
  proof_kind: 'REMOTE_FINALITY' | 'FINAL_NON_APPLICATION' | null;
  proof_observed_at: string | null;
  created_at: string;
  updated_at: string;
}
export interface ProviderIntegrationState {
  property_id: string;
  environment: ProviderIntegrationEnvironment;
  provider: 'EZREMS';
  support_scope: 'ROOM_TYPE_CHANGE';
  support_status: 'SUPPORTED' | 'UNSUPPORTED_ENVIRONMENT';
  source: 'CORE_PROVIDER_OPERATION_JOURNAL';
  observed_at: string;
  settings: {
    source: 'CORE_PROPERTY_SOURCE_MAPPING';
    project_mapping_configured: boolean;
  };
  operations: ProviderIntegrationOperation[];
}

const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const hasKeys = (value: Record<string, unknown>, keys: readonly string[]) =>
  Object.keys(value).length === keys.length &&
  keys.every((key) => Object.prototype.hasOwnProperty.call(value, key));
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '저장된 외부 연동 정보를 불러오지 못했습니다. 관리자에게 문의해 주세요.',
  });

function operation(value: unknown): value is ProviderIntegrationOperation {
  if (
    !record(value) ||
    !hasKeys(value, [
      'target_kind',
      'transport',
      'readback_matches',
      'terminal_state',
      'proof_kind',
      'proof_observed_at',
      'created_at',
      'updated_at',
    ]) ||
    !['MIDTERM', 'GENERAL'].includes(value.target_kind as string) ||
    !['UNKNOWN', 'DELIVERED'].includes(value.transport as string) ||
    !(value.readback_matches === null || typeof value.readback_matches === 'boolean') ||
    !['PENDING', 'UNKNOWN', 'APPLIED', 'NOT_APPLIED'].includes(value.terminal_state as string) ||
    !(
      value.proof_kind === null ||
      ['REMOTE_FINALITY', 'FINAL_NON_APPLICATION'].includes(value.proof_kind as string)
    ) ||
    !(value.proof_observed_at === null || isOffsetDateTime(value.proof_observed_at)) ||
    !isOffsetDateTime(value.created_at) ||
    !isOffsetDateTime(value.updated_at)
  )
    return false;
  return true;
}

function valid(
  value: unknown,
  propertyId: string,
  environment: ProviderIntegrationEnvironment,
): value is ProviderIntegrationState {
  if (
    !record(value) ||
    !hasKeys(value, [
      'property_id',
      'environment',
      'provider',
      'support_scope',
      'support_status',
      'source',
      'observed_at',
      'settings',
      'operations',
    ]) ||
    !isUuid(value.property_id) ||
    value.property_id.toLowerCase() !== propertyId.toLowerCase() ||
    value.environment !== environment ||
    value.provider !== 'EZREMS' ||
    value.support_scope !== 'ROOM_TYPE_CHANGE' ||
    !['SUPPORTED', 'UNSUPPORTED_ENVIRONMENT'].includes(value.support_status as string) ||
    value.source !== 'CORE_PROVIDER_OPERATION_JOURNAL' ||
    !isOffsetDateTime(value.observed_at) ||
    !record(value.settings) ||
    !hasKeys(value.settings, ['source', 'project_mapping_configured']) ||
    value.settings.source !== 'CORE_PROPERTY_SOURCE_MAPPING' ||
    typeof value.settings.project_mapping_configured !== 'boolean' ||
    !Array.isArray(value.operations) ||
    value.operations.length > 10 ||
    !value.operations.every(operation)
  )
    return false;

  if (
    (environment === 'dev' && value.support_status !== 'SUPPORTED') ||
    (environment === 'prod' &&
      (value.support_status !== 'UNSUPPORTED_ENVIRONMENT' || value.operations.length !== 0))
  )
    return false;

  return true;
}

export async function readProviderIntegrationState(
  client: ServiceClient,
  propertyId: string,
  environment: ProviderIntegrationEnvironment,
  signal?: AbortSignal,
): Promise<ProviderIntegrationState> {
  if (!isUuid(propertyId) || !['dev', 'prod'].includes(environment)) throw invalid();
  const body = await client.get<unknown>(
    `admin/properties/${propertyId}/provider-integration-state`,
    { signal },
  );
  if (!valid(body, propertyId, environment)) throw invalid();
  return body;
}
